import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { tenantApi } from '../services/api';
import { Icon } from '@iconify/react';
import ClinicAdminLayout from '../components/admin/ClinicAdminLayout';
import DokterModal from '../components/admin/DokterModal';

// Dummy data untuk dokter
const initialDokterData = [
    {
        id: 1,
        nama: 'Dr. Andrew Collins',
        poli: 'Poli Umum',
        noTelp: '(021) 1234 5678',
        jadwal: 'Senin-Jumat 08.00-16.00',
        status: 'aktif',
        foto: null
    },
    {
        id: 2,
        nama: 'Dr. Emily Watson',
        poli: 'Poli Gigi',
        noTelp: '(021) 1234 5678',
        jadwal: 'Senin-Jumat 08.00-16.00',
        status: 'non-aktif',
        foto: null
    }
];

const ClinicAdminDokter = () => {
    const { slug: routeSlug } = useParams();
    const slug =
        routeSlug ||
        (() => {
            try {
                return JSON.parse(localStorage.getItem('user') || '{}')?.klinik?.slug;
            } catch {
                return null;
            }
        })();

    const [dokterList, setDokterList] = useState([]);
    const [poliList, setPoliList] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingDokter, setEditingDokter] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [sortConfig, setSortConfig] = useState({ key: 'nama_dokter', direction: 'asc' });
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 10;

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL?.replace('/api', '') || 'http://localhost:8000';

    const fetchData = async () => {
        if (!slug) return;
        try {
            setIsLoading(true);
            const [dokterRes, poliRes] = await Promise.all([
                tenantApi(slug).dokter.getAll(),
                tenantApi(slug).poli.getAll(),
            ]);

            if (dokterRes.data.success) {
                setDokterList(dokterRes.data.data);
            }
            if (poliRes.data.success) {
                setPoliList(poliRes.data.data);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!slug) return;
        fetchData();
    }, [slug]);

    const handleOpenModal = () => {
        setEditingDokter(null);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingDokter(null);
    };

    const handleEditDokter = (dokter) => {
        setEditingDokter(dokter);
        setIsModalOpen(true);
    };

    const handleDeleteDokter = async (id) => {
        if (window.confirm('Apakah Anda yakin ingin menghapus dokter ini?')) {
            try {
                await tenantApi(slug).dokter.delete(id);
                fetchData();
            } catch (error) {
                console.error("Error deleting dokter:", error);
            }
        }
    };

    const handleSubmit = async (formData) => {
        try {
            const user = JSON.parse(localStorage.getItem('user'));
            const id_klinik = user?.klinik?.id_klinik;

            if (!id_klinik) {
                alert("Error: Data klinik tidak ditemukan. Silakan login ulang.");
                return;
            }

            const normalizedEmail = (formData.email || '').trim().toLowerCase();
            const normalizedUsername = (formData.username || '').trim();
            const normalizedNamaDokter = (formData.nama_dokter || '').trim();
            const normalizedPassword = formData.password || '';

            if (!normalizedNamaDokter) {
                alert('Nama dokter wajib diisi.');
                return;
            }
            if (!normalizedEmail) {
                alert('Email wajib diisi.');
                return;
            }
            if (!normalizedUsername) {
                alert('Username wajib diisi.');
                return;
            }
            if (/\s/.test(normalizedUsername)) {
                alert('Username tidak boleh mengandung spasi.');
                return;
            }
            if (!editingDokter && normalizedPassword.length < 6) {
                alert('Password minimal 6 karakter.');
                return;
            }
            if (editingDokter && normalizedPassword && normalizedPassword.length < 6) {
                alert('Password minimal 6 karakter.');
                return;
            }

            const currentId = editingDokter?.id_dokter;
            const emailTaken = dokterList.some((dokter) =>
                dokter.id_dokter !== currentId &&
                (dokter.email || dokter.user?.email || '').toLowerCase() === normalizedEmail
            );
            if (emailTaken) {
                alert('Email sudah digunakan dokter lain.');
                return;
            }

            const usernameTaken = dokterList.some((dokter) =>
                dokter.id_dokter !== currentId &&
                (dokter.user?.username || '').toLowerCase() === normalizedUsername.toLowerCase()
            );
            if (usernameTaken) {
                alert('Username sudah digunakan dokter lain.');
                return;
            }

            // Create FormData object to handle file upload
            const dataToSend = new FormData();
            dataToSend.append('id_klinik', id_klinik);
            dataToSend.append('nama_dokter', normalizedNamaDokter);
            dataToSend.append('email', normalizedEmail);
            if (formData.id_poli !== '' && formData.id_poli != null) {
                dataToSend.append('id_poli', formData.id_poli);
            }
            dataToSend.append('username', normalizedUsername);

            // Only append password if it's provided (important for edit)
            if (normalizedPassword) {
                dataToSend.append('password', normalizedPassword);
            }

            dataToSend.append('jadwal', formData.jadwal);
            dataToSend.append('status', formData.status);

            if (formData.foto instanceof File) {
                dataToSend.append('foto', formData.foto);
            }

            // If we are editing and want to use PUT/PATCH with FormData in Laravel,
            // we often need to use POST and spoof the method
            if (editingDokter) {
                await tenantApi(slug).dokter.updateWithForm(editingDokter.id_dokter, dataToSend);
            } else {
                await tenantApi(slug).dokter.createWithForm(dataToSend);
            }

            fetchData();
            handleCloseModal();
        } catch (error) {
            console.error("Error saving dokter:", error);
            const fieldErrors = error.response?.data?.errors;
            const firstFieldError = fieldErrors
                ? Object.values(fieldErrors).flat().find(Boolean)
                : null;
            const msg =
                firstFieldError ||
                error.response?.data?.message ||
                'Terjadi kesalahan saat menyimpan data';
            alert(msg);
            throw error;
        }
    };

    // Helper to get poli name from id/object
    const getPoliName = (poliId, doctorPoli) => {
        if (doctorPoli && doctorPoli.nama_poli) return doctorPoli.nama_poli;
        // Fallback search
        const poli = poliList.find(p => p.id_poli === poliId);
        return poli ? poli.nama_poli : '-';
    };

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const sortedDoctors = [...dokterList].sort((a, b) => {
        const direction = sortConfig.direction === 'asc' ? 1 : -1;
        const av = sortConfig.key === 'poli' ? getPoliName(a.id_poli, a.poli) : (a?.[sortConfig.key] ?? '');
        const bv = sortConfig.key === 'poli' ? getPoliName(b.id_poli, b.poli) : (b?.[sortConfig.key] ?? '');
        return String(av).localeCompare(String(bv), 'id', { numeric: true }) * direction;
    });

    const totalPages = Math.max(1, Math.ceil(sortedDoctors.length / perPage));
    const paginatedDoctors = sortedDoctors.slice((currentPage - 1) * perPage, currentPage * perPage);

    if (!slug) {
        return (
            <ClinicAdminLayout activePage="dokter" title="Dokter">
                <p className="text-sm text-gray-500">
                    Slug klinik tidak ditemukan. Gunakan URL <code className="bg-gray-100 px-1 rounded">/nama-klinik/clinic-admin/dokter</code>.
                </p>
            </ClinicAdminLayout>
        );
    }

    return (
        <ClinicAdminLayout activePage="dokter" title="Dokter">
            {/* Dokter Table Card */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-50 p-8">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-gray-800 leading-none">Dokter</h3>
                    <button
                        onClick={handleOpenModal}
                        className="px-6 py-3 bg-gradient-to-r from-[#06B6D4] to-[#038DA5] text-white 
                        font-bold text-sm rounded-xl hover:opacity-90 transition-all shadow-lg 
                        shadow-cyan-500/30 flex items-center gap-2"
                    >
                        <Icon icon="material-symbols:add" className="text-lg" />
                        Tambah Dokter
                    </button>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gray-50">
                                <th onClick={() => handleSort('nama_dokter')} className="cursor-pointer px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider rounded-l-xl">Nama Dokter</th>
                                <th onClick={() => handleSort('poli')} className="cursor-pointer px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Poli</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Username</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Jadwal</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider rounded-r-xl text-center">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50 font-body">
                            {isLoading ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center text-gray-400">Loading...</td>
                                </tr>
                            ) : paginatedDoctors.map((dokter) => (
                                <tr key={dokter.id_dokter} className="hover:bg-gray-50 transition-colors group">
                                    <td className="px-6 py-5">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-gray-100 rounded-full flex-shrink-0 border border-gray-50 overflow-hidden">
                                                {dokter.foto ? (
                                                    <img
                                                        src={dokter.foto.startsWith('http') ? dokter.foto : `${API_BASE_URL}${dokter.foto}`}
                                                        alt={dokter.nama_dokter}
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center">
                                                        <Icon icon="material-symbols:person" className="text-gray-400 text-xl" />
                                                    </div>
                                                )}
                                            </div>
                                            <span className="font-bold text-sm text-gray-700">{dokter.nama_dokter}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-5 text-sm font-medium text-gray-500">{getPoliName(dokter.id_poli, dokter.poli)}</td>
                                    <td className="px-6 py-5">
                                        <div className="flex flex-col gap-0.5">
                                            <span className="text-sm font-medium text-gray-800">{dokter.user?.username || '-'}</span>
                                            <span className="text-xs text-gray-400 truncate max-w-[200px]" title={dokter.email || dokter.user?.email || ''}>
                                                {dokter.email || dokter.user?.email || '-'}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-5 text-sm font-medium text-gray-500">{dokter.jadwal}</td>
                                    <td className="px-6 py-5">
                                        <div className={`w-[80px] h-7 flex items-center justify-center text-white text-[10px] font-black rounded-lg ${dokter.status === 'aktif'
                                            ? 'bg-[#0DBC94]'
                                            : 'bg-[#F64E60]'
                                            }`}>
                                            {dokter.status === 'aktif' ? 'Aktif' : 'Non-Aktif'}
                                        </div>
                                    </td>
                                    <td className="px-6 py-5">
                                        <div className="flex items-center justify-center gap-2">
                                            <button
                                                onClick={() => handleEditDokter(dokter)}
                                                className="w-8 h-8 flex items-center justify-center bg-white border border-gray-100 rounded-lg text-gray-400 hover:text-[#06B6D4] hover:border-[#06B6D4] transition-all"
                                            >
                                                <Icon icon="material-symbols:edit-note-outline" className="text-xl" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteDokter(dokter.id_dokter)}
                                                className="w-8 h-8 flex items-center justify-center bg-white border border-gray-100 rounded-lg text-gray-400 hover:text-red-500 hover:border-red-500 transition-all"
                                            >
                                                <Icon icon="material-symbols:delete-outline" className="text-xl" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {!isLoading && paginatedDoctors.length === 0 && (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center text-gray-400">
                                        <Icon icon="material-symbols:person-off-outline" className="text-5xl mx-auto mb-3" />
                                        <p className="text-sm font-body">Belum ada dokter terdaftar</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="mt-4 flex items-center justify-end gap-2">
                    <button disabled={currentPage === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} className="px-3 py-1 border rounded disabled:opacity-50">Prev</button>
                    <span className="text-sm text-gray-500">{currentPage}/{totalPages}</span>
                    <button disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
                </div>
            </div>

            {/* Dokter Modal */}
            <DokterModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleSubmit}
                editData={editingDokter}
                poliList={poliList}
            />
        </ClinicAdminLayout>
    );
};

export default ClinicAdminDokter;
